/**
 * AvatarMenu — Topbar-rechts Dropdown mit Profil/Einstellungen (bald) +
 * Abmelden. Kanonische Position für Logout (Slack/GitHub/Notion-Convention).
 *
 * Eingeführt in Slice-3 Redesign (2026-05-09). Slice-4 (2026-05-09)
 * erweitert um full WAI-ARIA-menu Pattern: Roving tabIndex, ↑/↓/Home/End
 * mit Skip-disabled + Wrapping, Focus-Reset on Close (kein
 * position-memory).
 *
 * - Esc-Handler ruft `event.stopPropagation()` damit Layout's Esc-Handler
 *   (Drawer-close) NICHT parallel feuert.
 * - Outside-click `mousedown`-handler checkt BEIDE refs (menu + trigger),
 *   damit click auf den open-trigger nicht in close→reopen race endet.
 * - Z-50 lokal innerhalb Topbar-SC (Topbar ist sticky z-30 = eigener
 *   stacking context, AvatarMenu z-50 lokal stacked effektiv über content
 *   inkl CardActions z-20).
 * - MenuItem-Type ist discriminated union — production-default ist 2×
 *   disabled + 1× separator + 1× action. Tests dürfen `items`-prop mit
 *   3× action für realistic Roving-Coverage übergeben.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { I } from '../lib/icons';
import { Pill } from './ui/Pill';
import { redirectToToolsSettings } from '../lib/tools-login';

/**
 * Discriminated MenuItem-Union.
 * - `action`: clickable, focusable, optional `tone` für danger-styling.
 * - `disabled`: nicht-focusable im Roving, mit "Bald"-Pill.
 * - `separator`: nicht-focusable, dünne border-Linie.
 */
export type MenuItem =
  | {
      kind: 'action';
      id: string;
      label: string;
      onClick: () => void;
      tone?: 'default' | 'danger';
      icon?: ReactNode;
    }
  | { kind: 'disabled'; id: string; label: string; pillLabel?: string }
  | { kind: 'separator'; id: string };

type Props = {
  user: { displayName: string };
  onLogout: () => void;
  /**
   * Öffnungsrichtung des Dropdowns. `'bottom-right'` (Default) = klassisch
   * unter dem Trigger, rechtsbündig (Topbar-Erbe). `'top-left'` = nach OBEN
   * aufklappen, linksbündig — für den Trigger unten in der Sidebar, wo „nach
   * unten" aus dem Viewport liefe (2026-07-19).
   */
  placement?: 'bottom-right' | 'top-left';
  /**
   * Optionales Styling des Avatar-Trigger-Buttons. Default = kompakter Kreis
   * für hellen Grund. Der Sidebar-Footer überschreibt für einen größeren,
   * kontrastreichen Kreis auf dunklem Grün (2026-07-19).
   */
  triggerClassName?: string;
  /** Optional: Test-Subkomponenten können items-Liste überschreiben um
   *  Roving-Logic mit 2+ enabled items zu verifizieren. Production-Default
   *  ist 2× disabled + 1× separator + 1× action. */
  items?: MenuItem[];
  /** Optionaler nicht-interaktiver Kopf oben im Menü (z.B. die eingeloggte
   *  Email). Presentational — zählt NICHT zu den focusable menuitems. */
  header?: ReactNode;
  /** Startet die Orientierungs-Tour. Nur gesetzt → Eintrag „Tour starten" in den
   *  Default-Items. Bewusst ein Callback statt eines Imports hier: AvatarMenu
   *  hängt auch in tools + Transfer, und ein Import von `lib/onboarding-tour`
   *  zöge die DAM-Tour-Texte in deren Bundles (`bundle:check:transfer`). */
  onStartTour?: () => void;
};

const LogoutIcon = <I.logout size={14} />;

/** Default-Trigger — kompakter Kreis für hellen Grund (Topbar-Erbe). */
const DEFAULT_TRIGGER_CLASS =
  'w-7 h-7 rounded-full bg-brand-green-700 text-brand-cream flex items-center justify-center text-xs2 font-semibold hover:opacity-90 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-cream';

// Production-default items. Das frühere disabled „Einstellungen (Bald)"-Item ist
// entfernt (2026-06-09) — Einstellungen leben in der Sidebar.
//
// P1c PR-C (Must 12, D-P1c-13): „Profil" zeigt jetzt auf die tools-Settings
// (Identität liegt beim IdP). Das ist Cross-Origin → `location.replace()` im
// Helper, KEIN wouter-`navigate` (das kennt nur den App-eigenen History-Stack).
// Damit braucht `defaultItems` keinen navigate-Parameter mehr.
//
// Und weil die Tour-Karte mit der DAM-ProfilePage weggefallen ist, zieht der
// Tour-Neustart hierher: „Tour starten" ist der letzte verbliebene Einstieg in
// die Orientierungs-Tour und bleibt bewusst DAM-lokal (die Tour erklärt die
// DAM-Oberfläche, nicht tools).
function defaultItems(onLogout: () => void, onStartTour?: () => void): MenuItem[] {
  return [
    {
      kind: 'action',
      id: 'profile',
      label: 'Profil',
      onClick: () => redirectToToolsSettings('profile'),
    },
    ...(onStartTour
      ? [
          {
            kind: 'action',
            id: 'start-tour',
            label: 'Tour starten',
            onClick: onStartTour,
          } satisfies MenuItem,
        ]
      : []),
    { kind: 'separator', id: 'sep-1' },
    {
      kind: 'action',
      id: 'logout',
      label: 'Abmelden',
      onClick: onLogout,
      tone: 'danger',
      icon: LogoutIcon,
    },
  ];
}

/** Indices der focusable (= action) items. */
function focusableIndices(items: MenuItem[]): number[] {
  return items
    .map((it, i) => (it.kind === 'action' ? i : -1))
    .filter((i) => i >= 0);
}

export function AvatarMenu({
  user,
  onLogout,
  items: itemsProp,
  header,
  placement = 'bottom-right',
  triggerClassName = DEFAULT_TRIGGER_CLASS,
  onStartTour,
}: Props) {
  const items = itemsProp ?? defaultItems(onLogout, onStartTour);
  const focusable = focusableIndices(items);
  const initialFocusIndex = focusable[0] ?? -1;

  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(initialFocusIndex);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const initials = user.displayName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');

  // Focus-Reset on Close: focusedIndex auf initial zurücksetzen, damit
  // Reopen IMMER beim ersten non-disabled startet (kein position-memory).
  // WAI-ARIA-konform.
  const closeMenu = useCallback(() => {
    setOpen(false);
    setFocusedIndex(initialFocusIndex);
    triggerRef.current?.focus();
  }, [initialFocusIndex]);

  // Esc + Outside-Click. stopPropagation auf Esc damit Layout-handler
  // (Drawer-close) nicht parallel feuert.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu();
      }
    }
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        // Outside-click: close + reset, KEIN focus-restore (User wollte
        // ja woanders hin).
        setOpen(false);
        setFocusedIndex(initialFocusIndex);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, closeMenu, initialFocusIndex]);

  // Synchron Focus auf das aktive Item bei Open + bei focusedIndex-change.
  // Kein setTimeout — Roving-State ist deterministisch.
  useEffect(() => {
    if (!open) return;
    if (focusedIndex >= 0) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [open, focusedIndex]);

  // Open-Toggle: bei Open initialFocusIndex setzen damit erstes Item im
  // Roving-State (verifiziert von T8 — kein position-memory von letztem
  // close).
  function toggleOpen() {
    if (open) {
      closeMenu();
    } else {
      setFocusedIndex(initialFocusIndex);
      setOpen(true);
    }
  }

  // Roving-Navigation: ↑/↓/Home/End skipt non-action items, wrappt cyclic.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (focusable.length === 0) return;
    const currentPos = focusable.indexOf(focusedIndex);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextPos = currentPos < 0 ? 0 : (currentPos + 1) % focusable.length;
      setFocusedIndex(focusable[nextPos]!);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevPos =
        currentPos < 0
          ? focusable.length - 1
          : (currentPos - 1 + focusable.length) % focusable.length;
      setFocusedIndex(focusable[prevPos]!);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusedIndex(focusable[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusedIndex(focusable[focusable.length - 1]!);
    }
  }

  function handleActionClick(item: Extract<MenuItem, { kind: 'action' }>) {
    setOpen(false);
    setFocusedIndex(initialFocusIndex);
    item.onClick();
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Konto-Menü, eingeloggt als ${user.displayName}`}
        className={triggerClassName}
      >
        {initials}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Konto-Aktionen"
          onKeyDown={handleKeyDown}
          className={`absolute z-50 bg-white rounded-lg shadow-pop border border-ink-100 py-1 w-52 gf-pop-in ${
            placement === 'top-left'
              ? 'left-0 origin-bottom-left bottom-full mb-1.5'
              : 'right-0 origin-top-right top-full mt-1.5'
          }`}
        >
          {header != null && (
            <div role="presentation" className="border-b border-ink-100 px-3 py-2">
              <span className="block truncate text-xs2 font-mono text-brand-green-600">
                {header}
              </span>
            </div>
          )}
          {items.map((item, i) => {
            if (item.kind === 'separator') {
              return (
                <div
                  key={item.id}
                  role="separator"
                  className="border-t border-ink-100 my-1"
                />
              );
            }
            const isActive = i === focusedIndex;
            const tabIndex = isActive ? 0 : -1;
            if (item.kind === 'disabled') {
              return (
                <button
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role="menuitem"
                  aria-disabled="true"
                  tabIndex={tabIndex}
                  onClick={(e) => e.preventDefault()}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs text-left text-brand-green-800 opacity-60 cursor-not-allowed"
                >
                  <span>{item.label}</span>
                  {item.pillLabel && <Pill tone="sand">{item.pillLabel}</Pill>}
                </button>
              );
            }
            // action
            const danger = item.tone === 'danger';
            return (
              <button
                key={item.id}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={tabIndex}
                onClick={() => handleActionClick(item)}
                className={
                  danger
                    ? 'w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-red-700 hover:bg-red-50 focus:bg-red-50 focus:outline-none transition'
                    : 'w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-brand-green-800 hover:bg-ink-100 focus:bg-ink-100 focus:outline-none transition'
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
